import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function PlayerScoresModal({ open, onClose, player, bars, onUpdate }) {
  const { adminDisplayEnabled } = useAuth();
  const isAdmin = adminDisplayEnabled;
  const [playerScores, setPlayerScores] = useState([]);
  const [playerAchievements, setPlayerAchievements] = useState([]);
  const [playerTraps, setPlayerTraps] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [allTraps, setAllTraps] = useState([]);
  const [claimedAchievementIds, setClaimedAchievementIds] = useState(new Set());
  const [claimedTrapIds, setClaimedTrapIds] = useState(new Set());
  const [playerPhotos, setPlayerPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Scores, 1 = Handicaps, 2 = Traps
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (open && player && bars) {
      // Reset state when player changes
      setPlayerScores([]);
      setPlayerAchievements([]);
      setPlayerTraps([]);
      setAllAchievements([]);
      setAllTraps([]);
      setClaimedAchievementIds(new Set());
      setClaimedTrapIds(new Set());
      setError(null);
      setSuccess(false);
      
      // Build array of scores per bar for this player
      const scoresByBar = bars.map((bar) => {
        const score = bar.scores?.find(
          s => s.name === player.name
        );
        return {
          barId: bar.id,
          barName: bar.name,
          barAddress: bar.address,
          barPar: bar.par,
          score: score ? score.score : null,
          scoreIndex: score ? bar.scores.indexOf(score) : -1,
        };
      });
      setPlayerScores(scoresByBar);
      
      // Fetch player achievements and photos
      if (player.id) {
        fetchPlayerAchievements(player.id);
        fetchPlayerPhotos(player.id);
      }
    } else if (!open) {
      // Reset when modal closes
      setPlayerScores([]);
      setPlayerAchievements([]);
      setPlayerTraps([]);
      setAllAchievements([]);
      setAllTraps([]);
      setClaimedAchievementIds(new Set());
      setClaimedTrapIds(new Set());
      setPlayerPhotos([]);
      setActiveTab(0); // Reset to Scores tab
    }
  }, [open, player, bars]);

  // Check if tabs container can scroll
  useEffect(() => {
    const checkScroll = () => {
      if (tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth - 1
        );
      }
    };

    checkScroll();
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      // Check after a short delay to ensure DOM is ready
      setTimeout(checkScroll, 100);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      }
    };
  }, [open, activeTab]);

  const fetchPlayerAchievements = async (userId) => {
    try {
      // Fetch all achievements
      const allAchievementsResponse = await axios.get(`${API_URL}/api/achievements`);
      const all = allAchievementsResponse.data;
      
      // Separate handicaps and traps
      const handicaps = all.filter(a => !a.is_trap || a.is_trap === false);
      const traps = all.filter(a => a.is_trap === true);
      
      // Sort handicaps by par_score in ascending order
      const sortedHandicaps = [...handicaps].sort((a, b) => {
        if (a.par_score !== b.par_score) {
          return a.par_score - b.par_score;
        }
        // Secondary sort by id for stability
        return a.id.localeCompare(b.id);
      });
      
      // Sort traps by par_score in descending order
      const sortedTraps = [...traps].sort((a, b) => {
        if (a.par_score !== b.par_score) {
          return b.par_score - a.par_score;
        }
        // Secondary sort by id for stability
        return a.id.localeCompare(b.id);
      });
      
      setAllAchievements(sortedHandicaps);
      setAllTraps(sortedTraps);
      
      // Fetch player's claimed achievements
      const claimedResponse = await axios.get(`${API_URL}/api/users/${userId}/achievements`);
      const claimed = claimedResponse.data;
      
      // Separate claimed handicaps and traps
      const claimedHandicapIds = new Set(
        claimed.filter(a => {
          const achievement = all.find(ach => ach.id === a.id);
          return achievement && (!achievement.is_trap || achievement.is_trap === false);
        }).map(a => a.id)
      );
      const claimedTrapIds = new Set(
        claimed.filter(a => {
          const achievement = all.find(ach => ach.id === a.id);
          return achievement && achievement.is_trap === true;
        }).map(a => a.id)
      );
      
      setClaimedAchievementIds(claimedHandicapIds);
      setClaimedTrapIds(claimedTrapIds);
      
      // Set player achievements and traps to all (for display)
      setPlayerAchievements(sortedHandicaps);
      setPlayerTraps(sortedTraps);
    } catch (error) {
      console.error('Error fetching player achievements:', error);
      setAllAchievements([]);
      setAllTraps([]);
      setClaimedAchievementIds(new Set());
      setClaimedTrapIds(new Set());
      setPlayerAchievements([]);
      setPlayerTraps([]);
    }
  };

  const fetchPlayerPhotos = async (userId) => {
    try {
      setPhotosLoading(true);
      const response = await axios.get(`${API_URL}/api/users/${userId}/photos`);
      setPlayerPhotos(response.data);
    } catch (error) {
      console.error('Error fetching player photos:', error);
      setPlayerPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const handleScoreChange = (barId, newScore) => {
    if (!isAdmin) return;
    
    const updatedScores = playerScores.map((item) => {
      if (item.barId === barId) {
        const parsedScore = newScore === '' ? null : parseInt(newScore);
        return { ...item, score: isNaN(parsedScore) ? null : parsedScore };
      }
      return item;
    });
    setPlayerScores(updatedScores);
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Update scores for each bar that has changed
      for (const item of playerScores) {
        if (item.scoreIndex >= 0 && item.score !== null) {
          // Update existing score
          await axios.put(
            `${API_URL}/api/bars/${item.barId}/scores/${item.scoreIndex}`,
            { score: item.score }
          );
        }
      }

      setSuccess(true);
      
      // Refresh data
      if (onUpdate) {
        await onUpdate();
      }

      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating scores:', error);
      setError('Failed to update scores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAchievement = async (achievementId) => {
    if (!isAdmin || !player.id) return;

    setLoading(true);
    setError(null);

    try {
      await axios.delete(`${API_URL}/api/users/${player.id}/achievements/${achievementId}`);
      await fetchPlayerAchievements(player.id);
      
      if (onUpdate) {
        await onUpdate();
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error('Error removing achievement:', error);
      setError('Failed to remove achievement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimAchievement = async (achievementId) => {
    if (!isAdmin || !player.id) return;

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/users/${player.id}/achievements/${achievementId}`);
      await fetchPlayerAchievements(player.id);
      
      if (onUpdate) {
        await onUpdate();
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error('Error claiming achievement:', error);
      setError('Failed to claim achievement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!player) return null;

  const barTotalScore = playerScores.reduce((sum, item) => sum + (item.score || 0), 0);
  // Only count claimed achievements (handicaps) in total score
  const achievementTotalScore = Array.from(claimedAchievementIds).reduce((sum, achievementId) => {
    const achievement = allAchievements.find(a => a.id === achievementId);
    return sum + (achievement?.par_score || 0);
  }, 0);
  // Count claimed traps in total score
  const trapTotalScore = Array.from(claimedTrapIds).reduce((sum, trapId) => {
    const trap = allTraps.find(t => t.id === trapId);
    return sum + (trap?.par_score || 0);
  }, 0);
  const totalScore = barTotalScore + achievementTotalScore + trapTotalScore;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={player.avatar}
              alt={player.name}
              sx={{ width: 48, height: 48 }}
            >
              {player.name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6">{player.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Score: {totalScore} {(achievementTotalScore !== 0 || trapTotalScore !== 0) && (
                  <span style={{ color: (achievementTotalScore + trapTotalScore) < 0 ? '#4caf50' : 'inherit' }}>
                    ({barTotalScore} bars {achievementTotalScore !== 0 && (
                      <span>{achievementTotalScore < 0 ? '-' : '+'} {Math.abs(achievementTotalScore)} handicaps</span>
                    )} {trapTotalScore !== 0 && (
                      <span>{trapTotalScore < 0 ? '-' : '+'} {Math.abs(trapTotalScore)} traps</span>
                    )})
                  </span>
                )}
                {playerPhotos.length > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    • {playerPhotos.length} photo{playerPhotos.length !== 1 ? 's' : ''}
                  </span>
                )}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Scores updated successfully!
          </Alert>
        )}
        
        <Box 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            mb: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Left arrow indicator */}
          {canScrollLeft && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                backgroundColor: 'background.paper',
                borderRadius: '50%',
                boxShadow: 2,
                pointerEvents: 'none',
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box>
          )}
          {/* Right arrow indicator */}
          {canScrollRight && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                backgroundColor: 'background.paper',
                borderRadius: '50%',
                boxShadow: 2,
                pointerEvents: 'none',
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box>
          )}
          {/* Left fade indicator */}
          {canScrollLeft && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 40,
                background: (theme) => `linear-gradient(to right, ${theme.palette.background.paper}, transparent)`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Right fade indicator */}
          {canScrollRight && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: 40,
                background: (theme) => `linear-gradient(to left, ${theme.palette.background.paper}, transparent)`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          )}
          <Box
            ref={tabsContainerRef}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome/Safari
              },
              WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minWidth: 'fit-content',
                '& .MuiTabs-scrollButtons': {
                  display: 'none',
                },
              }}
            >
              <Tab label="Scores" />
              <Tab label="Handicaps" />
              <Tab label="Traps" />
              {playerPhotos.length > 0 && (
                <Tab label={`Photos (${playerPhotos.length})`} />
              )}
            </Tabs>
          </Box>
        </Box>

        {activeTab === 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
              Course Scores
            </Typography>
        <List>
          {playerScores.map((item) => (
            <ListItem
              key={item.barId}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 2,
              }}
            >
              <ListItemAvatar>
                <Chip
                  label={`Par ${item.barPar}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={item.barName}
                secondary={item.barAddress}
                sx={{ flex: 1 }}
              />
              {isAdmin ? (
                <TextField
                  type="number"
                  value={item.score || ''}
                  onChange={(e) => handleScoreChange(item.barId, e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ width: 100 }}
                  inputProps={{ min: 0 }}
                  placeholder="No score"
                />
              ) : (
                <Typography variant="h6" sx={{ minWidth: 60, textAlign: 'right' }}>
                  {item.score !== null ? item.score : '-'}
                </Typography>
              )}
            </ListItem>
          ))}
        </List>
        
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: '2px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Total Score
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {totalScore}
          </Typography>
        </Box>
          </>
        )}

        {activeTab === 1 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
              Handicaps
            </Typography>
            <List>
              {playerAchievements.length > 0 ? (
                playerAchievements.map((achievement) => {
                  const isClaimed = claimedAchievementIds.has(achievement.id);
                  return (
                    <ListItem
                      key={achievement.id}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1.5,
                        opacity: isClaimed ? 1 : 0.5,
                        filter: isClaimed ? 'none' : 'grayscale(0.7)',
                        transition: 'opacity 0.2s, filter 0.2s',
                        '&:hover': {
                          opacity: 1,
                          filter: 'none',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        {achievement.image_url ? (
                          <Box
                            component="img"
                            src={achievement.image_url}
                            alt={achievement.name}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        ) : (
                          <Avatar sx={{ width: 48, height: 48 }}>
                            {achievement.name?.[0]}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={achievement.name}
                        secondary={achievement.description || `Par: ${achievement.par_score}`}
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`Par: ${achievement.par_score}`}
                          size="small"
                          color={achievement.par_score < 0 ? "success" : "primary"}
                          sx={{ fontWeight: 600 }}
                        />
                        {isAdmin && (
                          <>
                            {isClaimed ? (
                              <IconButton
                                onClick={() => handleRemoveAchievement(achievement.id)}
                                color="error"
                                size="small"
                                title="Remove Achievement"
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : (
                              <IconButton
                                onClick={() => handleClaimAchievement(achievement.id)}
                                color="success"
                                size="small"
                                title="Claim Achievement"
                                sx={{
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                  },
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                      </Box>
                    </ListItem>
                  );
                })
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No handicaps available"
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
            
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total Score
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {totalScore}
              </Typography>
            </Box>
          </>
        )}

        {activeTab === 2 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
              Traps
            </Typography>
            <List>
              {playerTraps.length > 0 ? (
                playerTraps.map((trap) => {
                  const isClaimed = claimedTrapIds.has(trap.id);
                  return (
                    <ListItem
                      key={trap.id}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1.5,
                        opacity: isClaimed ? 1 : 0.5,
                        filter: isClaimed ? 'none' : 'grayscale(0.7)',
                        transition: 'opacity 0.2s, filter 0.2s',
                        '&:hover': {
                          opacity: 1,
                          filter: 'none',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        {trap.image_url ? (
                          <Box
                            component="img"
                            src={trap.image_url}
                            alt={trap.name}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        ) : (
                          <Avatar sx={{ width: 48, height: 48 }}>
                            {trap.name?.[0]}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={trap.name}
                        secondary={trap.description || `Par: ${trap.par_score}`}
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`Par: ${trap.par_score}`}
                          size="small"
                          color={trap.par_score < 0 ? "success" : "primary"}
                          sx={{ fontWeight: 600 }}
                        />
                        {isAdmin && (
                          <>
                            {isClaimed ? (
                              <IconButton
                                onClick={() => handleRemoveAchievement(trap.id)}
                                color="error"
                                size="small"
                                title="Remove Trap"
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : (
                              <IconButton
                                onClick={() => handleClaimAchievement(trap.id)}
                                color="success"
                                size="small"
                                title="Claim Trap"
                                sx={{
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                  },
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                      </Box>
                    </ListItem>
                  );
                })
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No traps available"
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
            
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total Score
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {totalScore}
              </Typography>
            </Box>
          </>
        )}

        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Photos ({playerPhotos.length})
            </Typography>
            {photosLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : playerPhotos.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No photos yet.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {playerPhotos.map((photo) => (
                  <Grid item xs={6} sm={4} md={3} key={photo.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 6,
                        },
                      }}
                      onClick={() => window.open(`${API_URL}${photo.url}`, '_blank')}
                    >
                      <CardMedia
                        component="img"
                        image={`${API_URL}${photo.url}`}
                        alt={`Photo by ${photo.user_name}`}
                        sx={{
                          aspectRatio: '1',
                          objectFit: 'cover',
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Total Score
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {totalScore}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      {isAdmin && (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default PlayerScoresModal;

